import React, { useState, useContext, useRef } from "react";
import { ToastContext } from "components/dashboard/common/Toast/context/ToastContextProvider";

import { updateCurrentAnalyticsFilter } from "store/analytics/actions";
import { apiRoute } from "services";
import { useSelector, useDispatch } from "react-redux";
import { MonthDay, getErrorMessage, momentFormatDuration } from "utils/helper";
import { arrangeSolutionTypeData, getDatesBasedOnDuration } from "../common/helpers";
import { dataQueryStatus } from "utils";

import AnalyticsBarChart from "../common/AnalyticsBarChart/AnalyticsBarChart";
import { getCurrentAnalyticsCustomDates } from "../common/helpers";
import AnalyticsFilters from "../common/AnalyticsFilters/AnalyticsFilters";
import { teamAnalyticsRequestFilters } from "../common/enum";
import { useEffect } from "react";
import { ANALYTICS_TEAM_VOL_REQ_TYPES } from "../AnalyticsTeamVolume/enums";
import API from "services/lib/api";
import LiteBarChart from "../common/AnalyticsBarChart/LiteBarChart/LiteBarChart";

const { ERROR } = dataQueryStatus;

const AccurateAnalyticsTeamEfficiency = () => {

    const [isLoading, setIsLoading] = useState(false);
    const [chartLoading, setChartLoading] = useState(true);
    const [useAIAssistance, toggleAIAssistance] = useState(false);
    const [selectedOptions, setSelectedOptions] = useState([]);
    const [options, setOptions] = useState([]);
    const [chartData, setChartData] = useState([]);
    const [resolutionChartData, setResolutionChartData] = useState([]);
    const [fcrChartData, setFcrChartData] = useState([]);

    const [overallStats, setOverallStats] = useState({
        response: {
            agentAvg: 0,
            aiAvg: 0,
            trends: { agent: {}, ai: {} },
            memberAverages: {},
            avgResponsesOptions: []
        },
        resolution: {
            agentAvg: 0,
            trends: { agent: {} },
            memberAverages: {},
            avgResponsesOptions: []
        },
        fcr: {
            overallRate: 0,
            trends: { overall: {} },
            memberAverages: {},
            avgResponsesOptions: []
        }
    });
    const toastMessage = useContext(ToastContext);
    const dispatch = useDispatch();

    const {
        currentAnalyticsFilter,
        currentAnalyticsCustomDate,
    } = useSelector((state) => state.analytics);

    const { startDate, endDate } = getCurrentAnalyticsCustomDates(
        currentAnalyticsFilter,
        currentAnalyticsCustomDate
    ) || {};

    const {
        analyticsRequestType,
        analyticsRequestDuration,
        analyticsRequestValue,
    } = currentAnalyticsFilter || {};


    const calculateResponseTimeFrequency = (instances, timeRange) => {
        if (!instances?.length || timeRange <= 0) return 0;

        // Sort time ranges and merge overlapping ones
        const timeRanges = instances
            .map(item => ({
                min: parseFloat(item.min_response_time_minutes),
                max: parseFloat(item.max_response_time_minutes)
            }))
            .filter(range => !isNaN(range.min) && !isNaN(range.max))
            .sort((a, b) => a.min - b.min);

        if (!timeRanges.length) return 0;

        // Merge overlapping ranges
        const mergedRanges = timeRanges.reduce((acc, curr) => {
            if (!acc.length) return [curr];
            const last = acc[acc.length - 1];

            if (curr.min <= last.max) {
                last.max = Math.max(last.max, curr.max);
                return acc;
            }
            return [...acc, curr];
        }, []);

        // Calculate total coverage
        const totalCoverage = mergedRanges.reduce((sum, range) =>
            sum + (range.max - range.min), 0);

        return Math.min((totalCoverage / timeRange) * 100, 100);
    };

    const hasTicketInfo = analyticsRequestType === teamAnalyticsRequestFilters.TICKET_CLASSIFICATION;
    const analyticsResultTimeMetric = useRef({
        averageFirstResponseTime: "Sec",
        averageResolutionTime: "Sec",
        averageResponseTime: "Sec",
        aiAverageFirstResponseTime: "Sec",
        aiAverageResponseTime: "Sec",
        total_response_time: "Hour",
        ai_response_time: "Sec",
    });

    const updateRequest = (name, value) => {
        dispatch(
            updateCurrentAnalyticsFilter({
                [name]: value,
            })
        );
    };

    const handleSetOptions = async (options) => {
        await setOptions(options);
    };

    const minutesToHours = value => Math.round((parseFloat(value) / 60) * 100) / 100;

    const compileChartData = async (data, keyName, valName) => {
        if (!data || data.length === 0) return [];

        if (data?.length > 0) {
            const dateSeries = getDatesBasedOnDuration(
                analyticsRequestDuration
            );

            const mainArrayData = [];

            const optionValues = options.map(({ value }) => {
                return value;
            });

            const newValsArray =
                analyticsRequestValue?.length > 0
                    ? analyticsRequestValue?.split(",")
                    : optionValues;


            const overallStats = data.reduce((acc, item) => {
                const min = parseFloat(item.min_response_time_minutes);
                const max = parseFloat(item.max_response_time_minutes);


                return {
                    ...acc,
                    min: Math.min(acc.min, isNaN(min) ? Infinity : min),
                    max: Math.max(acc.max, isNaN(max) ? -Infinity : max),
                };
            }, {
                min: Infinity,
                max: -Infinity,

            });

       
            const timeRange = overallStats.max - overallStats.min;

            // ticketVolTimeMetric.current = useMinuteAsTime ? "Min" : "Sec";

            dateSeries?.forEach(async (dt) => {
                //assignedTeamId

                let valInstances = data.filter((ad) =>
                    new Date(ad.date).toDateString() === new Date(dt).toDateString()
                );

                const dailyFrequency = calculateResponseTimeFrequency(valInstances, timeRange);

                let originalObj = Object.assign(
                    {},
                    ...newValsArray.map((y) => {
                        return { [y]: 0 };
                    })
                );

                let result = {};

                if (analyticsRequestType === "TICKET_SOLUTION_TYPE") {
                    result = Object.assign(
                        originalObj,
                        ...valInstances.map((valData) => {
                            return arrangeSolutionTypeData(
                                "averageResponseTime",
                                valData
                            );
                        })
                    );
                } else {
                    // Calculate total and AI-specific values
                    const totalValue = valInstances.reduce((sum, o) =>
                        sum + minutesToHours(o[valName] || 0), 0);

                    const aiValue = valInstances
                        .filter(o => o.responder_type === "AI")
                        .reduce((sum, o) =>
                            sum + minutesToHours(o[valName] || 0), 0);

                    result = Object.assign(
                        originalObj,
                        ...valInstances.map((o) => {
                            const naminng = o[keyName] || "all"
                            return {
                                [`${naminng}_FREQ`]: Math.round(dailyFrequency),
                                valInstances,
                                [`${naminng}`]: minutesToHours(parseInt(o[valName]) || 0),
                                originalData: o,
                                total_response_time: parseFloat(totalValue.toFixed(2)),
                                y_total_response_time: Math.round(totalValue),
                                ai_response_time: parseFloat(aiValue.toFixed(2)),
                                total_response_time_FREQ: Math.round(dailyFrequency),
                            };
                        })
                    );
                }

                mainArrayData.push({ ...result, dateValue: MonthDay(dt) });
            });

            return mainArrayData;
        }

    };

    const compileResolutionData = async (data, keyName) => {
        console.log("resolutionData", data);
        if (!data || data.length === 0) return [];
       
        const maxDate = new Date(Math.max(...data.map(d => new Date(d.date_trunc))));
        
        // Process data for chart display
        const resultInDateRange = data.reduce((acc, d) => {
            const startDate = new Date(d.date_trunc);
            const endDate = new Date(startDate);
            endDate.setDate(startDate.getDate() + 7);
            
            const dateValue = `${MonthDay(startDate)} - ${MonthDay(endDate)}`;
            const hasEndDateInRange = new Date(d.date_trunc) < maxDate;
            
            if (hasEndDateInRange) {
                if (!acc[dateValue]) {
                    acc[dateValue] = {
                        dateValue,
                        memberStats: {}
                    };
                }
                
                const memberId = d[keyName] || 'all';
                if (!acc[dateValue].memberStats[memberId]) {
                    acc[dateValue].memberStats[memberId] = {
                        total: 0,
                        count: 0
                    };
                }
                
                acc[dateValue].memberStats[memberId].total += parseFloat(d.avg_resolution_time || 0);
                acc[dateValue].memberStats[memberId].count += 1;
            }
            return acc;
        }, {});

        const groupedResults = Object.values(resultInDateRange).map(group => ({
            dateValue: group.dateValue,
            ...Object.entries(group.memberStats).reduce((acc, [memberId, stats]) => ({
                ...acc,
                [memberId]: stats.total / stats.count
            }), {})
        }));

        return groupedResults;
    };

    const compileFCRData = async (data, keyName) => {
        if (!data || data.length === 0) return [];

        const dateSeries = getDatesBasedOnDuration(analyticsRequestDuration);
        const mainArrayData = [];

        const optionValues = options.map(({ value }) => value);
        const newValsArray = analyticsRequestValue?.length > 0
            ? analyticsRequestValue?.split(",")
            : optionValues;
        dateSeries?.forEach(dt => {
            let valInstances = data.filter(ad =>
                new Date(ad.dateValue).toDateString() === new Date(dt).toDateString()
            );

            let originalObj = Object.assign(
                {},
                ...newValsArray.map(y => ({ [y]: 0 }))
            );

            const aggregatedValues = valInstances.reduce((acc, o) => {
                const key = o[keyName] || "all";
                if (!acc[key]) {
                    acc[key] = {
                        fcr_rate: 0,
                        total_tickets: 0,
                        fcr_resolved: 0,
                        fcr_tickets: 0,
                        non_fcr_tickets: 0,
                        count: 0
                    };
                }
                // Ensure we're working with numbers by using Number() instead of parseFloat
                acc[key].fcr_rate += Number(o.daily_fcr_rate) || 0;
                acc[key].total_tickets += Number(o.total_tickets) || 0;
                acc[key].fcr_resolved += Number(o.fcr_resolved_tickets) || 0;
                acc[key].fcr_tickets += Number(o.fcr_tickets) || 0;
                acc[key].non_fcr_tickets += Number(o.non_fcr_tickets) || 0;
                acc[key].count++;
                return acc;
            }, {});

            // Create final result with averaged FCR rates
            const result = Object.assign(
                originalObj,
                ...Object.entries(aggregatedValues).map(([key, values]) => ({
                    [key]: values.fcr_rate / values.count,
                    fcr_rate: values.fcr_rate / values.count,
                    total_tickets: values.total_tickets,
                    fcr_resolved: values.fcr_resolved,
                    fcr_tickets: values.fcr_tickets,
                    non_fcr_tickets: values.non_fcr_tickets
                }))
            );

            mainArrayData.push({ ...result, dateValue: MonthDay(dt) });
        });

        return mainArrayData;
    };

    const processMetrics = (data, metricKey, isPercentage = false, isHours) => {
        if (!data?.length) return {
            agentAvg: 0,
            aiAvg: 0,
            trends: {},
            memberAverages: {},
            avgResponsesOptions: []
        };

        console.log({ data, metricKey })
      

        // Step 1: Group data by date and member
        const groupedData = data.reduce((acc, item) => {
            const date = new Date(item.date || item.date_trunc || item.dateValue)
                .toISOString().split('T')[0];
            const isAI = item.responder_type === 'AI';
            
            const value = isPercentage ? 
                parseFloat(item[metricKey] || 0) : 
                (isAI || isHours) ? item[metricKey] : minutesToHours(parseFloat(item[metricKey] || 0));
            const memberId = item[ANALYTICS_TEAM_VOL_REQ_TYPES?.[currentAnalyticsFilter?.analyticsRequestType]] || 'all';
            const ticketCount = parseInt(item.total_tickets || 1);

            // Initialize date entry
            if (!acc[date]) {
                acc[date] = {
                    agent: { total: 0, count: 0 },
                    ai: { total: 0, count: 0 },
                    members: {}
                };
            }

            // Track AI/Agent metrics
            const targetGroup = isAI ? acc[date].ai : acc[date].agent;
            targetGroup.total += value * ticketCount;
            targetGroup.count += ticketCount;

            // Track member-specific metrics
            if (!acc[date].members[memberId]) {
                acc[date].members[memberId] = { total: 0, count: 0 };
            }
            acc[date].members[memberId].total += value * ticketCount;
            acc[date].members[memberId].count += ticketCount;

            return acc;
        }, {});

        console.log(`${metricKey} : `, { groupedData });
        // Step 2: Calculate daily averages
        const sortedDates = Object.keys(groupedData).sort();
        const dailyAverages = sortedDates.map(date => ({
            date,
            agent: groupedData[date].agent.count > 0 ? 
                groupedData[date].agent.total / groupedData[date].agent.count : null,
            ai: groupedData[date].ai.count > 0 ? 
                groupedData[date].ai.total / groupedData[date].ai.count : null,
            members: Object.entries(groupedData[date].members).reduce((acc, [id, stats]) => ({
                ...acc,
                [id]: stats.count > 0 ? stats.total / stats.count : null
            }), {})
        }));

        console.log(`${metricKey} : `, { dailyAverages });

        // Step 3: Calculate overall averages
        const overallStats = sortedDates.reduce((acc, date) => {
            const dayStats = groupedData[date];
            return {
                agent: {
                    total: acc.agent.total + dayStats.agent.total,
                    count: acc.agent.count + dayStats.agent.count
                },
                ai: {
                    total: acc.ai.total + dayStats.ai.total,
                    count: acc.ai.count + dayStats.ai.count
                },
                members: Object.entries(dayStats.members).reduce((memberAcc, [id, stats]) => ({
                    ...memberAcc,
                    [id]: {
                        total: (memberAcc[id]?.total || 0) + stats.total,
                        count: (memberAcc[id]?.count || 0) + stats.count
                    }
                }), acc.members)
            };
        }, { agent: { total: 0, count: 0 }, ai: { total: 0, count: 0 }, members: {} });

        // Step 4: Calculate trends using weighted moving average
        const calculateWeightedTrend = (originalValues) => {
            if (originalValues.length < 2) return { change: 0, trend: 'neutral' };

            const values = originalValues.filter(Boolean);
            console.log(`${metricKey}:`,{ values, originalValues });
            const validValues = values.filter(v => v !== null);
            if (validValues.length < 2) return { change: 0, trend: 'neutral' };

            const firstValue = validValues[0];
            const lastValue = validValues[validValues.length - 1];
            const percentDiff = ((Math.max(lastValue, firstValue) - Math.min(lastValue, firstValue)) / (firstValue+lastValue)) * 100;
            const change = lastValue > firstValue;
            console.log(`${metricKey}:`,{ firstValue, lastValue, change, percentDiff});
            
            return {
                change: percentDiff,
                trend: (change ? 'up' : 'down')
            };
        };

        const agentTrendValues = dailyAverages.map(d => d.agent);
        const aiTrendValues = dailyAverages.map(d => d.ai);
        console.log('Trend Values:', {
            agent: (agentTrendValues).filter(Boolean),
            ai: (aiTrendValues).filter(Boolean),
            memberValues: Object.entries(overallStats.members).reduce((acc, [id]) => ({
                ...acc,
                [id]: dailyAverages.map(d => d.members[id]).filter(Boolean)
            }), {})
        });

        return {
            agentAvg: overallStats.agent.count > 0 ? 
                overallStats.agent.total / overallStats.agent.count : 0,
            aiAvg: overallStats.ai.count > 0 ? 
                overallStats.ai.total / overallStats.ai.count : 0,
            trends: {
                agent: calculateWeightedTrend(dailyAverages.map(d => d.agent)),
                ai: calculateWeightedTrend(dailyAverages.map(d => d.ai))
            },
            memberAverages: Object.entries(overallStats.members).reduce((acc, [id, stats]) => ({
                ...acc,
                [id]: {
                    average: stats.count > 0 ? stats.total / stats.count : 0,
                    trend: calculateWeightedTrend(dailyAverages.map(d => d.members[id]))
                }
            }), {}),
            avgResponsesOptions: selectedOptions.map(option => ({
                ...option,
                average: {
                    average: overallStats.members[option.value]?.count > 0 ? 
                        overallStats.members[option.value].total / overallStats.members[option.value].count : 0
                }
            }))
        };
    };

    const getAnalyticsData = async () => {
        try {
            if (startDate === "" || endDate === "") {
                return;
            }
            setIsLoading(true);
            setChartLoading(true);
            const commaAnalyticsValue = "";

            const url = apiRoute?.getTicketsResponseTimeAnalytics;
            const res = await API.get(url, {
                params: {
                    ...currentAnalyticsFilter,
                    analyticsRequestValue: commaAnalyticsValue,
                    startDate,
                    endDate,
                    analyticsRequestType: analyticsRequestType || undefined,
                },
            });

            if (res.status === 200) {
                const { data } = res.data;

                const [responseChartData, resolutionChartData, fcrChartData] = await Promise.all([
                    compileChartData(data?.responseTimes, ANALYTICS_TEAM_VOL_REQ_TYPES?.[currentAnalyticsFilter?.analyticsRequestType], 'avg_response_time_minutes'),
                    compileResolutionData(data?.resolutionTimes, ANALYTICS_TEAM_VOL_REQ_TYPES?.[currentAnalyticsFilter?.analyticsRequestType]),
                    compileFCRData(data?.fcrRates, ANALYTICS_TEAM_VOL_REQ_TYPES?.[currentAnalyticsFilter?.analyticsRequestType])
                ]);

                setChartData(responseChartData);
                setResolutionChartData(resolutionChartData);
                setFcrChartData(fcrChartData);
        

                const overallMetrics = {
                    response: processMetrics(data?.responseTimes, 'avg_response_time_minutes'),
                    resolution: processMetrics(data?.resolutionTimes, 'avg_resolution_time', false, true),
                    fcr: processMetrics(data?.fcrRates, 'daily_fcr_rate', true)
                };

                console.log("overallMetrics", overallMetrics);
                // Update overall stats
                setOverallStats(overallMetrics);


            }
        } catch (err) {
            console.log(err);

            if (
                err?.message ===
                "Cannot destructure property 'baseURL' of 'originalRequest' as it is undefined."
            ) {
                return "";
            }
            let errorMssg = getErrorMessage(err);
            toastMessage(errorMssg, ERROR);
        } finally {
            setIsLoading(false);
            setChartLoading(false);
        }
    };

    useEffect(() => {
        getAnalyticsData()
        // eslint-disable-next-line
    }, [])


    return (
        <div className='padded__layout analytics-team-efficiency'>
            <AnalyticsFilters
                defaultRequest={currentAnalyticsFilter}
                currentAnalyticsCustomDate={currentAnalyticsCustomDate}
                updateRequest={updateRequest}
                handleSubmit={getAnalyticsData}
                isLoading={isLoading}
                selectedOptions={selectedOptions}
                setSelectedOptions={setSelectedOptions}
                {...{
                    options,
                    setOptions: handleSetOptions,
                }}
                showAIButton={true}
                toggleAIAssistance={(state) =>
                    toggleAIAssistance(!useAIAssistance)
                }
            />

            <div>
                <div className='analytics-team-efficiency__chart'>
                    <AnalyticsBarChart
                        chartTitle='Response Time Analysis'
                        chartData={chartData}
                        isLoading={chartLoading}
                        strokeData={[
                            ...((selectedOptions)?.length ? [] : [{
                                name: "Agent Response Time", 
                                value: "total_response_time",
                                color: "#6837ef"
                            }]),
                            ...selectedOptions
                        ]}
                        hasTicketInfo={hasTicketInfo}
                        allStrokeData={options}

                        xDataKey1="dateValue"
                        // yDataKey1="total_response_time"
                        yDataKey2="y_total_response_time"


                        yAxisLabel1="Average Response Time (hours)"
                        xAxisLabel1="Date"

                        yAxisLabel2="Average Response Time (hours)"
                        xAxisLabel2="Frequency"

                        xDomain2={[0, 100]}
                        hasFreqKey

                        useAIAssistance={useAIAssistance}
                     
                        tooltipFields={[
                            { key: 'total_response_time', label: 'Average Response Time', unit: 'hrs' },
                            { key: 'ai_response_time', label: 'Average AI Response', unit: 'hrs' },
                            ...selectedOptions.map((option) => (
                                {
                                    key: option.value,
                                    label: `${option.label}'s Response Time`,
                                    unit: 'hrs'
                                } 
                            ))
                        ]}
                        useDefaultOptions={analyticsRequestType?.length === 0}
                        isOverlapped
                        skipCompute
                        overviews={[
                            {
                                title: "Average Response Time",
                                text: `${Number(overallStats.response.agentAvg || 0).toFixed(2)} hrs`,
                                isHigh: overallStats.response.trends?.agent?.trend === 'up',
                                percent: Number(overallStats.response.trends?.agent?.change || 0).toFixed(2)
                            },
                            // Filter avgResponsesOptions based on selectedOptions
                            ...(selectedOptions
                                .filter(selected => overallStats.response?.memberAverages?.[selected.value])
                                .map(option => ({
                                    title: `${option.label}'s Response Time`,
                                    text: `${Number(overallStats.response.memberAverages?.[option.value]?.average || 0).toFixed(2)} hrs`,
                                    isHigh: overallStats.response.memberAverages?.[option.value]?.trend?.trend === 'up',
                                    percent: Number(overallStats.response.memberAverages?.[option.value]?.trend?.change || 0).toFixed(2)
                                }))
                            ),
                            useAIAssistance && {
                                title: "AI Response",
                                text: `${Number(overallStats.response.aiAvg || 0).toFixed(2)} Mins`,
                                isHigh: overallStats.response.trends?.ai?.trend === 'up',
                                percent: Number(overallStats.response.trends?.ai?.change || 0).toFixed(2)
                            }
                        ].filter(Boolean)}
                        timeMetric={analyticsResultTimeMetric.current[useAIAssistance ? 'ai_response_time' : 'total_response_time']}
                    />
                </div>

                {/* Resolution Time Chart */}

                <div className='analytics-team-efficiency__chart'>
                    <LiteBarChart
                        chartTitle='Resolution Time Analysis'
                        isLoading={chartLoading}
                        data={resolutionChartData}
                        bars={[
                            ...((selectedOptions)?.length ? [] : [{
                                name: "Average Resolution Time", 
                                value: "all",
                                color: "#00FF00"
                            }]),
                            ...selectedOptions
                        ]}
                        xAxisDataKey="dateValue"
                        overviews={[
                            {
                                title: "Average Resolution Time",
                                text: `${momentFormatDuration(Number(overallStats.resolution.agentAvg || 0).toFixed(2))}`,
                                isHigh: overallStats.resolution.trends?.agent?.trend === 'up',
                                percent: Number(overallStats.resolution.trends?.agent?.change || 0).toFixed(2)
                            },
                            // Filter and map selected options for resolution time
                            ...(selectedOptions
                                .filter(selected => overallStats.resolution?.memberAverages?.[selected.value])
                                .map(option => ({
                                    title: `${option.label}'s Average Resolution Time`,
                                    text: `${momentFormatDuration(Number(overallStats.resolution.memberAverages?.[option.value]?.average || 0).toFixed(2))}`,
                                    isHigh: overallStats.resolution.memberAverages?.[option.value]?.trend?.trend === 'up',
                                    percent: Number(overallStats.resolution.memberAverages?.[option.value]?.trend?.change || 0).toFixed(2)
                                }))
                            )
                        ]}
                    />
                </div>

                {/* FCR Chart */}
                <div className='analytics-team-efficiency__chart'>
                    <AnalyticsBarChart
                        chartTitle='First Contact Resolution Analysis'
                        chartData={fcrChartData}
                        isLoading={chartLoading}
                        strokeData={[
                            ...((selectedOptions)?.length ? [] : [ {
                                name: "FCR Rate",
                                value: "fcr_rate",
                                color: "#FF9500",
                            },]),
                            ...selectedOptions
                        ]}
                        hasTicketInfo={hasTicketInfo}
                        allStrokeData={options}
                        xDataKey1="dateValue"
                        yDataKey1="fcr_rate"
                        yDataKey2="total_tickets"
                        yAxisLabel1="FCR Rate (%)"
                        xAxisLabel1="Date"
                        yAxisLabel2="Total Tickets"
                        xAxisLabel2="Count"
                        useAIAssistance={useAIAssistance}
                        isOverlapped
                        skipCompute
                        overviews={[
                            {
                                title: "Average FCR",
                                text: `${Number(overallStats.fcr.agentAvg || 0).toFixed(2)}%`,
                                isHigh: overallStats.fcr.trends?.agent?.trend === 'up',
                                percent: Number(overallStats.fcr.trends?.agent?.change || 0).toFixed(2)
                            },
                            // Filter and map selected options for FCR
                            ...(selectedOptions
                                .filter(selected => overallStats.fcr?.memberAverages?.[selected.value])
                                .map(option => ({
                                    title: `${option.label}'s FCR`,
                                    text: `${Number(overallStats.fcr.memberAverages?.[option.value]?.average || 0).toFixed(2)}%`,
                                    isHigh: overallStats.fcr.memberAverages?.[option.value]?.trend?.trend === 'up',
                                    percent: Number(overallStats.fcr.memberAverages?.[option.value]?.trend?.change || 0).toFixed(2)
                                }))
                            )
                        ].filter(Boolean)}

                        tooltipFields={[
                            {
                                label: "FCR Rate",
                                key: "fcr_rate",
                                unit: "%"
                            },
                            ...selectedOptions.map((option) => (
                                {
                                    key: option.value,
                                    label: `${option.label}'s FCR Rate`,
                                    unit: '%'
                                } 
                            ))
                        ]}
                    />
                </div>
            </div>
        </div>
    )
}

export default AccurateAnalyticsTeamEfficiency;